/**
 * 卡组列表页面
 * 展示用户所有卡片（虚拟卡和实体卡）
 */

import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCardList } from '@/hooks'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import RefreshList from '@/components/RefreshList'
import Ripple from '@/components/Ripple'
import EmptyState from '@/components/EmptyState'
import SkeletonBox from '@/components/SkeletonBox'
import { formatThousands } from '@/utils'
import Divider from '@/components/Divider'

// 卡片状态映射
const CardStatusMap: Record<string, { text: string; color: string }> = {
  active: { text: '已激活', color: '#52C41A' },
  frozen: { text: '已冻结', color: '#FAAD14' },
  closed: { text: '已注销', color: '#FF4D4F' },
  pending: { text: '开卡中', color: '#1890FF' },
  pending_activation: { text: '待激活', color: '#1890FF' },
  expired: { text: '已过期', color: '#86909C' },
}

// 卡片类型映射
const CardTypeMap: Record<string, string> = {
  virtual_card: '虚拟卡',
  physical_card: '实体卡',
}

// 卡片项接口
interface CardItem {
  balance_limit?: BalanceLimit;
  item_id?: number
  masked_pan?: string
  status?: string
  form_factor?: string
  image?: string
  available_balance?: number
  card_issued?: boolean
  // 固定值字段（接口暂未更新）
  per_tran_limit?: number
  day_limit?: number
}

export default function CardList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { allCards = [], loading, actions } = useCardList()
  const [refreshing, setRefreshing] = useState(false)

  // 进入页面时刷新（不排除销卡项，排除正在下卡的项）
  useEffect(() => {
    actions.refreshCardList(false, true)
  }, [])

  // 下拉刷新（不排除销卡项，排除正在下卡的项）
  const onRefresh = useCallback(async () => {
    setRefreshing(true)
    await actions.refreshCardList(false, true)
    setRefreshing(false)
  }, [actions])

  // 点击卡片查看交易记录
  const handleCardClick = useCallback((card: CardItem) => {
    if (!card.item_id || !card.card_issued) return
    
    // 跳转到交易记录页面，携带卡片信息
    navigate(`/card-v2/consumption-records?cardId=${card.item_id}`, {
      state: {
        cardImage: card.image || '',
        cardNo: card.masked_pan || '',
        cardStatus: card.status || '',
        cardType: card.form_factor || ''
      }
    })
  }, [navigate])

  // 获取状态显示
  const getStatusDisplay = useCallback((status?: string) => {
    const statusInfo = CardStatusMap[status || ''] || { text: '-', color: '#86909C' }
    return statusInfo
  }, [])

  // 骨架屏
  const renderSkeleton = useCallback(() => {
    return (
      <div className="mx-5 mb-4 bg-white rounded-2xl p-4">
        <div className="flex items-center">
          <SkeletonBox className="w-24 h-16 rounded-lg flex-shrink-0" />
          <div className="flex-1 ml-4">
            <SkeletonBox className="w-32 h-5 rounded mb-2" />
            <SkeletonBox className="w-20 h-4 rounded" />
          </div>
          <SkeletonBox className="w-8 h-8 rounded-full" />
        </div>
        <div className="mt-4 pt-4 border-t border-[#F5F5F5]">
          <div className="flex justify-between mb-2">
            <SkeletonBox className="w-16 h-4 rounded" />
            <SkeletonBox className="w-24 h-4 rounded" />
          </div>
          <div className="flex justify-between">
            <SkeletonBox className="w-12 h-4 rounded" />
            <SkeletonBox className="w-24 h-4 rounded" />
          </div>
        </div>
      </div>
    )
  }, [])

  // 渲染卡片项
  const renderCardItem = useCallback((card: CardItem) => {
    const statusInfo = getStatusDisplay(card.status)
    const cardType = CardTypeMap[card.form_factor || ''] || '虚拟卡'
    const isClickable = card.card_issued && card.status !== 'pending'

    // 限额值
    const perTranLimit = card.balance_limit?.max_per_tran ?? 0
    const dayLimit = card.balance_limit?.daily_max_amt ?? 0
    
    // 格式化限额显示（负数表示不限额）
    const formatLimit = (value: number) => {
      if (value < 0) return t('不限额')
      return `${formatThousands(value)} USD`
    }

    return (
      <Ripple className="rounded-2xl mx-5 mb-4">
        <div 
          className={`bg-[#F9F9F9] rounded-2xl p-4 ${isClickable ? 'cursor-pointer' : 'opacity-80'}`}
          onClick={() => isClickable && handleCardClick(card)}
        >
          {/* 卡片信息 */}
          <div className="flex items-center">
            {/* 卡片图片 */}
            <div className="w-19.5 h-12.5 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-400 to-blue-600">
              {card.image ? (
                <img 
                  src={card.image} 
                  alt="card" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-white text-xs font-medium">ZISE</span>
                </div>
              )}
            </div>

            {/* 卡片详情 */}
            <div className="flex-1 ml-4 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#1D2129]">
                  {t(cardType)}
                </span>
                <span className="text-sm text-[#1D2129]">
                  **** {card.masked_pan?.slice(-4) || '****'}
                </span>
              </div>
              <div className="flex items-center mt-1">
                <span 
                  className="w-1.5 h-1.5 rounded-full mr-1.5"
                  style={{ backgroundColor: statusInfo.color }}
                />
                <span className="text-xs">
                  {t(statusInfo.text)}
                </span>
              </div>
            </div>

            {/* 右侧图标 */}
            {isClickable && (
              <div className="w-8 h-8 flex items-center justify-center">
                <img src="/images/card-v2/cards-detail.svg" alt="" className="w-6 h-6" />
              </div>
            )}
          </div>
          <Divider className="my-4 bg-[#EAECF0]" />

          {/* 限额信息 */}
          <div className="border-t border-[#F5F5F5]">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-[#667085]">{t('单笔限额')}</span>
              <span className="text-sm font-medium text-[#1D2129]">
                {formatLimit(perTranLimit)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#667085]">{t('日限额')}</span>
              <span className="text-sm font-medium text-[#1D2129]">
                {formatLimit(dayLimit)}
              </span>
            </div>
          </div>
        </div>
      </Ripple>
    )
  }, [t, getStatusDisplay, handleCardClick])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium">{t('我的卡片')}</span>}
      />

      <div className="bg-white flex flex-col flex-1 pt-4">
        <RefreshList
          data={allCards as CardItem[]}
          renderItem={renderCardItem}
          keyExtractor={(item, index) => `${item.item_id}-${index}`}
          loading={loading || refreshing}
          hasMore={false}
          onRefresh={onRefresh}
          renderSkeleton={renderSkeleton}
          skeletonCount={3}
          emptyComponent={
            <EmptyState 
              text={t('暂无卡片')}
              icon={<img src="/images/card-v2/card-no-records.svg" alt="" className="w-32 h-auto" />}
            />
          }
          finishedText={t('已经显示全部')}
          className="flex-1"
        />
      </div>
    </PageLayout>
  )
}

